import {
  AfterViewInit,
  Component,
  computed,
  contentChild,
  ElementRef,
  HostBinding,
  input,
  Signal,
  inject,
} from '@angular/core';
import { NgControl, ValidationErrors, Validators } from '@angular/forms';
import { FormControlErrorsComponent } from '../form-control-errors/form-control-errors.component';
import { FormControlHintComponent } from '../form-control-hint/form-control-hint.component';
import { FormControlRequiredAsterixComponent } from '../form-control-required-asterix/form-control-required-asterix.component';
import { LabelStyleDirective } from '../../../label/directives/label-style.directive';
import { isEmptyString, isNil, isNotNil, Nillable } from '@web/utils';
import { ControlReferenceNotFoundException } from '../../exceptions/control-reference-not-found.exception';
import { NgTemplateOutlet } from '@angular/common';
import { FloatLabel } from 'primeng/floatlabel';

@Component({
  selector: 'app-form-control',
  imports: [
    FormControlErrorsComponent,
    FormControlHintComponent,
    FormControlRequiredAsterixComponent,
    LabelStyleDirective,
    NgTemplateOutlet,
    FloatLabel,
  ],
  templateUrl: './form-control.component.html',
  styleUrls: ['./form-control.component.scss'],
})
export class FormControlComponent implements AfterViewInit {
  private readonly elementRef = inject(ElementRef);

  private static attributeId = 0;

  public readonly withBottomMargin = input<boolean>(true);
  public readonly underlineInputStyle = input<boolean>(false);
  public readonly floatLabel = input<boolean>(false);
  public readonly hint = input<Nillable<string>>();
  public readonly requirementHint = input<boolean>(true);
  public readonly label = input<string>();
  public readonly inputId = input<string>(this.attributeId);

  protected readonly ngControl = contentChild(NgControl);
  protected readonly shouldShowHint = this.shouldShowHintSignal();
  protected readonly shouldShowLabel = this.shouldShowLabelSignal();

  public constructor() {
    this.incrementAttributeId();
  }

  public get hasRequiredValidator(): boolean {
    return !!this.ngControl()?.control?.hasValidator(Validators.required);
  }

  public get errors(): Nillable<ValidationErrors> {
    if (this.ngControl()?.dirty) {
      return this.ngControl()?.errors;
    }

    return null;
  }

  @HostBinding('class.form-control--underline-style')
  protected get shouldAddUnderlineStyle(): boolean {
    return this.underlineInputStyle();
  }

  private get attributeId(): string {
    return `form-control-${FormControlComponent.attributeId}`;
  }

  public ngAfterViewInit(): void {
    this.requireFromControlReference();
    this.appendAttributeIdToHTMLInput();
  }

  private incrementAttributeId(): void {
    FormControlComponent.attributeId++;
  }

  private requireFromControlReference(): void {
    if (isNil(this.ngControl())) {
      throw new ControlReferenceNotFoundException(
        'The reference to "FormControl" was not pass by the content projection.',
      );
    }
  }

  private appendAttributeIdToHTMLInput(): void {
    const html = this.hostHTMLElement();

    html.querySelector('input')?.setAttribute('id', this.inputId());
  }

  private hostHTMLElement(): HTMLElement {
    return this.elementRef.nativeElement;
  }

  private shouldShowHintSignal(): Signal<boolean> {
    return computed(() => {
      const hint = this.hint();

      return isNotNil(hint && isEmptyString(hint));
    });
  }

  private shouldShowLabelSignal(): Signal<boolean> {
    return computed(() => {
      const label = this.label();

      return isNotNil(label && isEmptyString(label));
    });
  }
}
